import { Box, Button, Container, Paper, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import React from 'react';
import * as Sentry from '@sentry/react';
import { I18n } from 'utils/i18n';

class OmmnioSignErrorBoundary extends React.Component {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Send error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        component: 'OmmnioSign',
      },
    });
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: 4,
        pb: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container sx={{ maxWidth: 600 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2 }}>
            {I18n.t('OmmnioSign.Errors.UnexpectedError')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            {I18n.t('OmmnioSign.Errors.UnexpectedErrorDescription')}
          </Typography>

          {error && (
            <>
              <Button size="small" onClick={() => setShowDetails(!showDetails)}>
                {showDetails
                  ? I18n.t('OmmnioSign.Errors.HideDetails')
                  : I18n.t('OmmnioSign.Errors.ShowDetails')}
              </Button>
              {showDetails && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    textAlign: 'left',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  <strong>{error.toString()}</strong>
                  {errorInfo && errorInfo.componentStack && (
                    <pre style={{ margin: 0, marginTop: 8, fontSize: '0.75rem' }}>
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default OmmnioSignErrorBoundary;
